import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import {
  Card, Button, Tag, Modal, Form, Input, Switch, useApp, Popconfirm, QRCode, Image, Row, Col, Alert,
  PageHeader, StatCard, Tabs, Tooltip, EmptyState, cn, type StatTone,
} from '../../ui';
import {
  Plus, Pencil, Trash2, Star, Landmark, Building2, Stamp, MapPin, Phone, FileText,
  BadgeCheck, CheckCircle2, AlertTriangle, QrCode, Copy,
} from 'lucide-react';
import { request } from '../../api/client';
import { useUrlString } from '../../hooks/useUrlState';
import { FileUploadField } from '../../components/FileUploadField';

/**
 * COMPANY & BILLING SETUP — the seller's own identity, the marks it signs with, and the accounts it
 * is paid into. Everything on this page ends up PRINTED on a document, which is the idea the old
 * layout never carried: it was one flat wall of sixteen identical grey inputs, then two dashed grey
 * rectangles, then a table — nothing said which fields were the legally required ones, whether the
 * set-up was finished, or what any of it would look like on an invoice.
 *
 * Three ideas structure the rewrite:
 *   1. A masthead that renders the company AS a letterhead, so the page opens on the thing it makes.
 *   2. A readiness band — four tiles that count what is set vs missing and jump to the tab that
 *      fixes it. Gaps are named in amber rather than left silently blank.
 *   3. Tabs, because "who we are", "how we sign" and "where we are paid" are three separate jobs.
 * The form itself is grouped into five labelled blocks instead of one 16-field grid.
 */

// ── shared bits ───────────────────────────────────────────────────────────────────────────────────

/** Tone → the chip styling for a group heading / masthead pill. Mirrors StatCard's accent map. */
const CHIP: Record<string, string> = {
  brand: 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300',
  sky: 'bg-sky-500/10 text-sky-600 dark:text-sky-400',
  gold: 'bg-harvest-50 text-harvest-700 dark:bg-harvest-400/15 dark:text-harvest-300',
  violet: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
  neutral: 'bg-surface-3 text-muted',
};

/**
 * One labelled block of related fields.
 *
 * The profile form asks for sixteen things that belong to five different questions — who you are,
 * where you are, what your registrations are, how to reach you, and what text goes at the bottom of
 * a document. Flat, they were indistinguishable; grouped, each block is a short form you can finish.
 */
function FieldGroup({ icon, tone = 'brand', title, hint, children }: {
  icon: ReactNode; tone?: keyof typeof CHIP; title: string; hint?: string; children: ReactNode;
}) {
  return (
    <section className="border-t border-line pt-5 first:border-t-0 first:pt-0">
      <div className="mb-3 flex items-start gap-2.5">
        <span className={cn('grid size-8 shrink-0 place-items-center rounded-card [&>svg]:size-4', CHIP[tone])}>{icon}</span>
        <div className="min-w-0">
          <h3 className="type-label-strong text-content">{title}</h3>
          {hint && <p className="mt-0.5 type-caption text-muted">{hint}</p>}
        </div>
      </div>
      <Row gutter={16}>{children}</Row>
    </section>
  );
}

/** A value as it will PRINT — mono for anything a machine reads back (GSTIN, IFSC, A/c no.). */
function Mono({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn('font-mono tabular-nums tracking-tight', className)}>{children}</span>;
}

/**
 * One statutory number in the letterhead's definition row — label over value, the way a registration
 * is quoted on a real document. A missing one keeps its slot and says so quietly in amber: it must be
 * noticeable without turning the header into a row of warning badges.
 */
function IdField({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="min-w-0">
      <dt className="type-overline text-subtle">{label}</dt>
      <dd className="truncate">
        {value
          ? <Mono className="type-label-strong text-content">{value}</Mono>
          : (
            <span className="inline-flex items-center gap-1 type-label text-warning-600 dark:text-harvest-300">
              <AlertTriangle className="size-3.5" /> not set
            </span>
          )}
      </dd>
    </div>
  );
}

const copy = (text: string, done: () => void) => { navigator.clipboard?.writeText(text).then(done, () => {}); };

// ── the company letterhead, as the documents draw it ──────────────────────────────────────────────

/**
 * The masthead. Deliberately shaped like the header band of the invoice PDF (`buildHouseInvoicePdf`):
 * legal name large, trade name under it, then the registration numbers as pills. Reading this is how
 * you notice the PAN is blank BEFORE a customer notices it on their bill.
 */
function CompanyMasthead({ p, marks }: { p: any; marks: { seal?: string | null; sign?: string | null } }) {
  const place = [p?.city, p?.stateName, p?.pincode].filter(Boolean).join(', ');
  return (
    <div className="mb-4 overflow-hidden rounded-card border border-line bg-surface shadow-e1">
      {/* A hairline of brand at the top edge, not a saturated slab. A settings page that opens on a
          block of solid green reads as a marketing banner; a document letterhead is quiet paper with
          one rule on it, and that is the object this page is really editing. */}
      <div className="h-1 bg-linear-to-r from-brand-500 via-brand-600 to-brand-700" />
      {/* Shading, not elevation: a wash that fades from a brand tint at the top-left into the plain
          surface, so the header has depth while the card keeps its single hairline separator. */}
      <div className="flex flex-col gap-5 bg-linear-to-br from-brand-50/70 via-surface to-surface p-5 lg:flex-row lg:items-start lg:justify-between lg:gap-8 dark:from-brand-900/20 dark:via-surface dark:to-surface">
        <div className="min-w-0 flex-1">
          <div className="type-overline text-brand-600 dark:text-brand-400">Seller identity · printed on every document</div>
          <h2 className="mt-1.5 type-section-title text-content">{p?.legalName || <span className="text-subtle">Your company name</span>}</h2>
          {p?.tradeName && <div className="mt-0.5 type-body text-muted">trading as {p.tradeName}</div>}
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 type-caption text-muted">
            {place && <span className="inline-flex items-center gap-1.5"><MapPin className="size-3.5 shrink-0 text-subtle" />{place}</span>}
            {p?.tel && <span className="inline-flex items-center gap-1.5"><Phone className="size-3.5 shrink-0 text-subtle" />{p.tel}</span>}
            {p?.email && <a href={`mailto:${p.email}`} className="text-sky-600 hover:underline dark:text-sky-400">{p.email}</a>}
            {p?.website && (
              <a href={`https://${String(p.website).replace(/^https?:\/\//, '')}`} target="_blank" rel="noopener" className="text-sky-600 hover:underline dark:text-sky-400">
                {p.website}
              </a>
            )}
          </div>
          {/* The registrations as a definition row — the same four a buyer's accountant looks for. */}
          <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 border-t border-line pt-3 sm:grid-cols-4">
            <IdField label="GSTIN" value={p?.gstin} />
            <IdField label="PAN" value={p?.pan} />
            <IdField label="CIN" value={p?.cin} />
            <IdField label="UDYAM" value={p?.msmeUdyam} />
          </dl>
        </div>

        {/* The two marks as framed proofs, captioned. On white, because that is the paper they land on. */}
        <div className="flex shrink-0 gap-3">
          {([['seal', 'Common seal'], ['sign', 'Signature']] as const).map(([k, caption]) => (
            <figure key={k} className="w-28">
              <div className={cn(
                'grid h-20 place-items-center rounded-card p-2',
                marks[k] ? 'border border-line bg-white' : 'border border-dashed border-line bg-surface-2',
              )}>
                {marks[k]
                  ? <img src={marks[k] as string} alt={caption} className="max-h-16 max-w-full object-contain" />
                  : <span className="type-caption text-subtle">not set</span>}
              </div>
              <figcaption className="mt-1.5 text-center type-caption text-muted">{caption}</figcaption>
            </figure>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── tab 1: the profile form ───────────────────────────────────────────────────────────────────────

function CompanyProfileTab({ data, isLoading }: { data: any; isLoading: boolean }) {
  const qc = useQueryClient();
  const { message } = useApp();
  const [form] = Form.useForm();
  useEffect(() => { if (data) form.setFieldsValue(data); }, [data, form]);

  const saveMut = useConfirmedMutation({ title: 'Save the company profile?', content: 'These details head every invoice and PDF the system produces.', okText: 'Yes, save' }, {
    mutationFn: (vals: any) => request({ url: '/company/profile', method: 'PUT', data: vals }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['company-profile'] }); message.success('Company profile saved'); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to save profile'),
  });

  return (
    <Card loading={isLoading}>
      <Form form={form} layout="vertical" requiredMark="optional" onFinish={(v) => saveMut.mutate(v)}>
        <div className="space-y-5">
          <FieldGroup icon={<Building2 />} tone="brand" title="Identity" hint="The name the law knows you by — it heads every invoice and proforma.">
            <Col xs={24} md={12}><Form.Item name="legalName" label="Legal name" rules={[{ required: true }]}><Input placeholder="GREENBAY BIOTECH INTERNATIONAL PRIVATE LIMITED" /></Form.Item></Col>
            <Col xs={24} md={12}><Form.Item name="tradeName" label="Trade name" extra="Only if you trade under a different name."><Input placeholder="Optional" /></Form.Item></Col>
          </FieldGroup>

          <FieldGroup icon={<MapPin />} tone="sky" title="Registered address" hint="Your place of supply. The state code decides CGST+SGST vs IGST on a bill.">
            <Col span={24}><Form.Item name="address" label="Registered address" rules={[{ required: true }]}><Input.TextArea rows={2} placeholder="Regd. Office: 2B, Grant Lane, 3rd Floor, Room No. 304, Kolkata, West Bengal - 700012, India" /></Form.Item></Col>
            <Col xs={12} md={8}><Form.Item name="city" label="City"><Input placeholder="Kolkata" /></Form.Item></Col>
            <Col xs={12} md={8}><Form.Item name="stateName" label="State"><Input placeholder="West Bengal" /></Form.Item></Col>
            <Col xs={8} md={3}><Form.Item name="stateCode" label="State code"><Input placeholder="19" /></Form.Item></Col>
            <Col xs={16} md={5}><Form.Item name="pincode" label="Pincode"><Input placeholder="700012" /></Form.Item></Col>
          </FieldGroup>

          <FieldGroup icon={<BadgeCheck />} tone="violet" title="Statutory registrations" hint="A tax invoice without a GSTIN is not a tax invoice — these are the numbers a buyer's accountant looks for.">
            <Col xs={24} md={8}><Form.Item name="gstin" label="GSTIN"><Input placeholder="27AAFCG8670G1ZB" /></Form.Item></Col>
            <Col xs={12} md={8}><Form.Item name="cin" label="CIN"><Input placeholder="U74900WB2015PTC205115" /></Form.Item></Col>
            <Col xs={12} md={8}><Form.Item name="pan" label="PAN"><Input placeholder="AAFCG8670G" /></Form.Item></Col>
            <Col span={24}><Form.Item name="msmeUdyam" label="MSME (UDYAM) numbers" extra="Separate several with a pipe — they print in the document footer."><Input placeholder="WB-10-0000909 | OD-30-0022442" /></Form.Item></Col>
          </FieldGroup>

          <FieldGroup icon={<Phone />} tone="gold" title="Contact" hint="How a customer reaches you from the bill in their hand.">
            <Col xs={24} md={8}><Form.Item name="tel" label="Telephone"><Input placeholder="+91 (33) 4064 6022" /></Form.Item></Col>
            <Col xs={12} md={8}><Form.Item name="mobile" label="Mobile (on documents)"><Input placeholder="+91 98765 43210" /></Form.Item></Col>
            <Col xs={12} md={8}><Form.Item name="email" label="Email"><Input placeholder="support@greenbaybio.com" /></Form.Item></Col>
            <Col xs={24} md={8}><Form.Item name="website" label="Website"><Input placeholder="www.greenbaybio.com" /></Form.Item></Col>
          </FieldGroup>

          <FieldGroup icon={<FileText />} tone="neutral" title="Printed at the foot of a document" hint="Free text — it appears under the totals on invoices and proformas.">
            <Col span={24}><Form.Item name="terms" label="Terms &amp; conditions"><Input.TextArea rows={2} placeholder="e.g. Interest @24% p.a. will be charged on payments made after 60 days from the bill date." /></Form.Item></Col>
            <Col span={24}><Form.Item name="declaration" label="Declaration"><Input placeholder="Certified that the particulars given above are true &amp; correct" /></Form.Item></Col>
          </FieldGroup>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-line pt-4">
          <Button variant="primary" type="submit" loading={saveMut.isPending}>Save profile</Button>
          <span className="type-caption text-muted">Saved changes appear on the next document generated — existing PDFs are not rewritten.</span>
        </div>
      </Form>
    </Card>
  );
}

// ── tab 2: seal & signature ───────────────────────────────────────────────────────────────────────

/**
 * The two images that sign a document. Shown as the signature BLOCK a PDF actually draws — caption,
 * seal, signature, "Authorised Signatory" — so the upload has an obvious consequence instead of being
 * two grey rectangles labelled with field names.
 */
function MarksTab({ data, isLoading, legalName }: { data: any; isLoading: boolean; legalName?: string }) {
  const qc = useQueryClient();
  const { message } = useApp();
  const [form] = Form.useForm();
  useEffect(() => { if (data) form.setFieldsValue({ sealUrl: data.sealUrl, signatureUrl: data.signatureUrl }); }, [data, form]);
  const sealUrl = Form.useWatch('sealUrl', form);
  const signatureUrl = Form.useWatch('signatureUrl', form);

  const saveMut = useConfirmedMutation({ title: 'Save the seal and signature?', content: 'They appear on every invoice and PDF from now on.', okText: 'Yes, save' }, {
    // Send both (empty string clears) so removing an image on the page removes it from the documents.
    mutationFn: (v: any) => request({ url: '/company/profile/assets', method: 'PUT', data: { sealUrl: v.sealUrl ?? '', signatureUrl: v.signatureUrl ?? '' } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['company-profile'] }); message.success('Seal & signature saved'); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to save'),
  });

  const slot = (url: string | undefined, kind: 'seal' | 'signature') => (
    <div className={cn(
      'grid h-32 place-items-center rounded-card p-3 transition-colors',
      url ? 'border border-line bg-white' : 'border border-dashed border-line bg-surface-2',
    )}>
      {url
        ? <Image src={url} height={96} style={{ objectFit: 'contain' }} />
        : <span className="type-caption text-subtle">No {kind} uploaded — the document prints the plain text caption instead.</span>}
    </div>
  );

  return (
    <Form form={form} layout="vertical" onFinish={(v) => saveMut.mutate(v)}>
      <Row gutter={16}>
        <Col xs={24} lg={14}>
          <Card>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item label={<span className="inline-flex items-center gap-1.5"><Stamp className="size-4 text-violet-600 dark:text-violet-400" /> Common seal</span>}>
                  {slot(sealUrl, 'seal')}
                  <div className="mt-2">
                    <Form.Item name="sealUrl" noStyle>
                      <FileUploadField category="company-seal" buttonLabel="Upload seal" preview={false} size="small" />
                    </Form.Item>
                  </div>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label={<span className="inline-flex items-center gap-1.5"><Pencil className="size-4 text-sky-600 dark:text-sky-400" /> Authorised signature</span>}>
                  {slot(signatureUrl, 'signature')}
                  <div className="mt-2">
                    <Form.Item name="signatureUrl" noStyle>
                      <FileUploadField category="company-signature" buttonLabel="Upload signature" preview={false} size="small" />
                    </Form.Item>
                  </div>
                </Form.Item>
              </Col>
            </Row>
            <Alert
              type="info"
              showIcon
              message="A transparent PNG gives the cleanest result — a scan with a white background prints as a white box over the invoice rule."
            />
            <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-line pt-4">
              <Button variant="primary" type="submit" loading={saveMut.isPending}>Save seal &amp; signature</Button>
              <span className="type-caption text-muted">Applies to tax invoices, proformas and credit notes.</span>
            </div>
          </Card>
        </Col>

        {/* What the PDF draws, live. */}
        <Col xs={24} lg={10}>
          <Card title="On the document" extra={<span className="type-caption text-muted">signature block</span>}>
            {/* The one place a drop shadow is right: this is meant to read as a sheet of paper
                lying on the page, so it takes elevation INSTEAD of a border, never both. */}
            <div className="rounded-card bg-white p-4 text-[#111] shadow-card dark:bg-white">
              <div className="text-right">
                <div className="text-[11px] uppercase tracking-wide opacity-70">For</div>
                <div className="type-label-strong">{legalName || 'YOUR COMPANY NAME'}</div>
                <div className="relative mt-2 grid h-24 place-items-end justify-items-end">
                  {sealUrl && <img src={sealUrl} alt="" className="absolute left-2 top-0 max-h-20 opacity-90" />}
                  {signatureUrl
                    ? <img src={signatureUrl} alt="" className="max-h-16 object-contain" />
                    : <span className="self-end text-[11px] italic opacity-40">signature</span>}
                </div>
                <div className="mt-1 border-t border-[#ccc] pt-1 text-[11px]">Authorised Signatory</div>
              </div>
            </div>
            <p className="mt-3 type-caption text-muted">
              Both marks are optional. With neither, the block prints as plain text — which is valid, just unsigned-looking.
            </p>
          </Card>
        </Col>
      </Row>
    </Form>
  );
}

// ── tab 3: bank accounts ──────────────────────────────────────────────────────────────────────────

interface BankAccount {
  id: string;
  label?: string | null;
  accountHolderName: string;
  bankName: string;
  accountNumber: string;
  ifsc: string;
  branch?: string | null;
  upiId?: string | null;
  upiQrUrl?: string | null;
  notes?: string | null;
  isPrimary: boolean;
  isActive: boolean;
}

const upiString = (a: { upiId?: string | null; accountHolderName?: string }) =>
  a.upiId ? `upi://pay?pa=${encodeURIComponent(a.upiId)}&pn=${encodeURIComponent(a.accountHolderName || 'Green Bay Biotech')}&cu=INR` : '';

/**
 * One account as a passbook card rather than a table row.
 *
 * A table made every account look alike, which is exactly wrong here: ONE of them is printed on every
 * invoice and the others are dormant. The primary card is tinted and ribboned, an inactive one is
 * drained, and the QR that customers actually scan is visible without opening an edit dialog.
 */
function BankCard({ a, onEdit, onDelete, onPrimary, busy }: {
  a: BankAccount; onEdit: () => void; onDelete: () => void; onPrimary: () => void; busy?: boolean;
}) {
  const { message } = useApp();
  const upi = upiString(a);
  // MySQL hands tinyint columns back as 0/1, so `isPrimary && <Tag/>` renders a literal "0" beside
  // every non-primary bank name. Coerce once, here, rather than trusting the declared boolean type.
  const isPrimary = !!a.isPrimary;
  const isActive = !!a.isActive;
  const sub = [a.accountHolderName, a.label, a.branch].filter(Boolean).join(' · ');

  return (
    <div className={cn(
      // Resting state keeps ONE separator (the border) per the app-wide restyle; the shadow is a
      // HOVER affordance, and the primary account is shaded rather than outlined twice.
      'flex h-full min-w-0 flex-col rounded-card border transition-all duration-150 hover:shadow-card',
      isPrimary
        ? 'border-brand-500/45 bg-linear-to-b from-brand-50/80 to-surface dark:from-brand-900/25 dark:to-surface'
        : 'border-line bg-surface',
    )}>
      {/* Header: the mark, the name, and the one status that matters. */}
      <div className="flex items-start gap-3 p-4 pb-3">
        <span className={cn(
          'grid size-10 shrink-0 place-items-center rounded-card [&>svg]:size-5',
          isPrimary ? CHIP.brand : CHIP.neutral,
        )}>
          <Landmark />
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate type-body-strong text-content" title={a.bankName}>{a.bankName}</div>
          {sub && <div className="truncate type-caption text-muted" title={sub}>{sub}</div>}
        </div>
        {isPrimary
          ? <Tag tone="green" icon={<Star className="fill-current" />}>Primary</Tag>
          : !isActive
            ? <Tag tone="neutral">Inactive</Tag>
            : null}
      </div>

      {/* Body: the numbers on the left in one aligned column, the QR held to the right. */}
      <div className="flex items-start gap-4 border-t border-line px-4 py-3.5">
        <dl className="min-w-0 flex-1 space-y-2.5">
          <div className="min-w-0">
            <dt className="type-overline text-subtle">Account number</dt>
            <dd className="truncate"><Mono className="type-body-strong text-content">{a.accountNumber}</Mono></dd>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="min-w-0">
              <dt className="type-overline text-subtle">IFSC</dt>
              <dd className="truncate"><Mono className="type-label-strong text-content">{a.ifsc}</Mono></dd>
            </div>
            <div className="min-w-0">
              <dt className="type-overline text-subtle">UPI</dt>
              <dd className="truncate">
                {a.upiId
                  ? (
                    <button
                      type="button"
                      title="Copy UPI ID"
                      className="inline-flex max-w-full items-center gap-1.5 text-left hover:text-brand-700 dark:hover:text-brand-300"
                      onClick={() => copy(a.upiId as string, () => message.success('UPI ID copied'))}
                    >
                      <Mono className="truncate type-label-strong text-content">{a.upiId}</Mono>
                      <Copy className="size-3 shrink-0 text-subtle" />
                    </button>
                  )
                  : <span className="type-label text-subtle">—</span>}
              </dd>
            </div>
          </div>
        </dl>
        {/* The QR a customer scans off the invoice — an uploaded image wins, else it is generated. */}
        <div className={cn(
          'grid size-20 shrink-0 place-items-center rounded-card p-1.5',
          a.upiQrUrl || upi ? 'border border-line bg-white' : 'border border-dashed border-line bg-surface-2',
        )}>
          {a.upiQrUrl
            ? <img src={a.upiQrUrl} alt="UPI QR" className="max-h-full max-w-full object-contain" />
            : upi
              ? <QRCode value={upi} size={64} bordered={false} />
              : <QrCode className="size-5 text-subtle" />}
        </div>
      </div>

      {/* Footer: quiet icon actions, and the one line saying what this account DOES. */}
      <div className="mt-auto flex items-center justify-between gap-2 border-t border-line px-4 py-2.5">
        <span className="min-w-0 truncate type-caption text-muted">
          {isPrimary ? 'Printed on invoices & proformas' : isActive ? 'On file — not printed' : 'Kept for the record — cannot be made primary'}
        </span>
        <div className="flex shrink-0 items-center gap-0.5">
          {!isPrimary && isActive && (
            <Tooltip label="Make this the account printed on invoices">
              <Button variant="text" size="sm" icon={<Star size={16} />} aria-label="Set as primary" loading={busy} onClick={onPrimary} />
            </Tooltip>
          )}
          <Tooltip label="Edit account"><Button variant="text" size="sm" icon={<Pencil size={16} />} aria-label="Edit" onClick={onEdit} /></Tooltip>
          <Popconfirm title="Delete this account?" onConfirm={onDelete} okButtonProps={{ danger: true }}>
            <Tooltip label="Delete account">
              <Button variant="text" size="sm" icon={<Trash2 size={16} />} aria-label="Delete" className="text-danger-600! hover:bg-danger-500/10! dark:text-danger-500!" />
            </Tooltip>
          </Popconfirm>
        </div>
      </div>
    </div>
  );
}

// ── page ──────────────────────────────────────────────────────────────────────────────────────────

export function BankDetailsPage() {
  const qc = useQueryClient();
  const { message } = useApp();
  const [tab, setTab] = useUrlString('tab', 'profile');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<BankAccount | null>(null);
  const [form] = Form.useForm<BankAccount>();
  const upiQrUrl = Form.useWatch('upiQrUrl', form);
  const upiId = Form.useWatch('upiId', form);
  const holder = Form.useWatch('accountHolderName', form);

  // One fetch for the whole page (same react-query key the tabs read, so it is shared, not repeated):
  // the masthead and the readiness band both need the profile before either tab is opened.
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['company-profile'],
    queryFn: () => request<any>({ url: '/company/profile' }),
  });
  const { data: accounts, isLoading } = useQuery({
    queryKey: ['company-bank-accounts'],
    queryFn: () => request<BankAccount[]>({ url: '/company/bank-accounts' }),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['company-bank-accounts'] });
  const fail = (e: any) => message.error(e.response?.data?.error?.message ?? 'Something went wrong');

  const saveMut = useConfirmedMutation({ title: 'Save this bank account?', content: 'It can be shown on invoices for customers to pay into.', okText: 'Yes, save' }, {
    mutationFn: (vals: any) =>
      editing
        ? request({ url: `/company/bank-accounts/${editing.id}`, method: 'PUT', data: vals })
        : request({ url: '/company/bank-accounts', method: 'POST', data: vals }),
    onSuccess: () => { invalidate(); setOpen(false); message.success(editing ? 'Account updated' : 'Account added'); },
    onError: fail,
  });
  const primaryMut = useConfirmedMutation({ title: 'Make this the primary account?', content: 'It becomes the account printed on invoices and the UPI QR code.', okText: 'Yes, make it primary' }, {
    mutationFn: (id: string) => request({ url: `/company/bank-accounts/${id}/primary`, method: 'POST' }),
    onSuccess: () => { invalidate(); message.success('Primary account updated'); },
    onError: fail,
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => request({ url: `/company/bank-accounts/${id}`, method: 'DELETE' }),
    onSuccess: () => { invalidate(); message.success('Account deleted'); },
    onError: fail,
  });

  // Seeding is via the Form's initialValues + key (below), not setFieldsValue here: the Form remounts
  // on modal open and re-inits from initialValues, which would otherwise wipe a handler seed (blank) or
  // let a prior row's values leak in (stale). The backend auto-promotes the first account to primary.
  const openAdd = () => { setEditing(null); setOpen(true); };
  const openEdit = (a: BankAccount) => { setEditing(a); setOpen(true); };
  const submit = () => form.validateFields().then((v) => saveMut.mutate(v)).catch(() => undefined);

  const rows = accounts ?? [];

  /*
   * READINESS — what is set up and what is not.
   *
   * A settings page has no rows to rank, so the band counts COMPLETENESS instead: each tile is green
   * when its section is done and amber when something is missing, names the specific gap, and opens
   * the tab that fixes it. Missing fields here are invisible until they are missing on a customer's
   * invoice, which is the worst possible place to find out.
   */
  const readiness = useMemo(() => {
    const p = profile ?? {};
    const identity = [
      ['legal name', p.legalName], ['address', p.address], ['city', p.city],
      ['state', p.stateName], ['state code', p.stateCode], ['pincode', p.pincode],
    ] as const;
    const tax = [['GSTIN', p.gstin], ['PAN', p.pan], ['CIN', p.cin]] as const;
    const contact = [['phone', p.tel || p.mobile], ['email', p.email]] as const;
    const missingIdentity = identity.filter(([, v]) => !v).map(([k]) => k);
    const missingTax = tax.filter(([, v]) => !v).map(([k]) => k);
    const missingContact = contact.filter(([, v]) => !v).map(([k]) => k);
    const marks = [p.sealUrl, p.signatureUrl].filter(Boolean).length;
    const active = rows.filter((a) => a.isActive);
    const primary = rows.find((a) => a.isPrimary && a.isActive);
    return {
      missingIdentity: [...missingIdentity, ...missingContact],
      missingTax,
      marks,
      activeCount: active.length,
      primary,
    };
  }, [profile, rows]);

  const tile = (
    key: string,
    args: { label: string; value: ReactNode; hint: string; tone: StatTone; icon: ReactNode },
  ) => (
    <StatCard
      label={args.label} value={args.value} hint={args.hint} tone={args.tone} icon={args.icon}
      active={tab === key} onClick={() => setTab(key)}
    />
  );

  const previewUpi = upiString({ upiId, accountHolderName: holder });

  return (
    <div>
      <PageHeader
        icon={<Building2 />}
        eyebrow="Administration"
        title="Company & Billing Setup"
        subtitle="Who you are on a document, how you sign it, and the accounts customers pay into."
      />

      <CompanyMasthead p={profile} marks={{ seal: profile?.sealUrl, sign: profile?.signatureUrl }} />

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {tile('profile', {
          label: 'Company identity',
          value: readiness.missingIdentity.length ? `${readiness.missingIdentity.length} missing` : 'Complete',
          hint: readiness.missingIdentity.length ? `no ${readiness.missingIdentity.slice(0, 3).join(', ')}` : 'name, address and contact all set',
          tone: readiness.missingIdentity.length ? 'warning' : 'success',
          icon: readiness.missingIdentity.length ? <AlertTriangle size={18} /> : <CheckCircle2 size={18} />,
        })}
        {tile('profile', {
          label: 'Tax registrations',
          value: `${3 - readiness.missingTax.length} of 3`,
          hint: readiness.missingTax.length ? `no ${readiness.missingTax.join(', ')}` : 'GSTIN, PAN and CIN on file',
          tone: readiness.missingTax.includes('GSTIN') ? 'danger' : readiness.missingTax.length ? 'warning' : 'success',
          icon: <BadgeCheck size={18} />,
        })}
        {tile('marks', {
          label: 'Seal & signature',
          value: readiness.marks === 2 ? 'Both set' : readiness.marks === 1 ? '1 of 2' : 'None',
          hint: readiness.marks === 2 ? 'documents sign themselves' : 'documents print a plain text caption',
          tone: readiness.marks === 2 ? 'success' : readiness.marks === 1 ? 'warning' : 'neutral',
          icon: <Stamp size={18} />,
        })}
        {tile('banks', {
          // Counts every account, matching the number on the tab beside it. Showing only the ACTIVE
          // ones here read "2" against a tab saying "(3)" — two numbers under one word.
          label: 'Bank accounts',
          value: rows.length,
          hint: readiness.primary
            ? `${readiness.primary.bankName} prints on invoices${rows.length > readiness.activeCount ? ` · ${rows.length - readiness.activeCount} inactive` : ''}`
            : 'no primary account — invoices show no bank',
          tone: readiness.primary ? 'sky' : 'warning',
          icon: <Landmark size={18} />,
        })}
      </div>

      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          {
            key: 'profile',
            label: 'Company profile',
            icon: <Building2 />,
            children: <CompanyProfileTab data={profile} isLoading={profileLoading} />,
          },
          {
            key: 'marks',
            label: 'Seal & signature',
            icon: <Stamp />,
            children: <MarksTab data={profile} isLoading={profileLoading} legalName={profile?.legalName} />,
          },
          {
            key: 'banks',
            label: `Bank accounts${rows.length ? ` (${rows.length})` : ''}`,
            icon: <Landmark />,
            children: (
              <>
                <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
                  <p className="max-w-3xl type-body text-muted">
                    Shown on invoices &amp; proformas so retailers can pay. Only the <b className="text-content">primary</b> account
                    is printed — the rest are kept on file.
                  </p>
                  <Button variant="primary" icon={<Plus size={16} />} onClick={openAdd}>Add account</Button>
                </div>
                {!isLoading && rows.length === 0
                  ? (
                    <Card>
                      <EmptyState
                        title="No bank accounts yet"
                        description="Add one so invoices tell your customers where to pay. The first account becomes the primary automatically."
                        action={<Button variant="primary" icon={<Plus size={16} />} onClick={openAdd}>Add account</Button>}
                      />
                    </Card>
                  )
                  : (
                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                      {rows.map((a) => (
                        <BankCard
                          key={a.id}
                          a={a}
                          busy={primaryMut.isPending}
                          onEdit={() => openEdit(a)}
                          onDelete={() => deleteMut.mutate(a.id)}
                          onPrimary={() => primaryMut.mutate(a.id)}
                        />
                      ))}
                      {/* The add tile sits with the accounts, so "one more" is where your eye already
                          is. Deliberately NOT card-height — a full-size empty dashed box reads as a
                          card that failed to load. */}
                      <button
                        type="button"
                        onClick={openAdd}
                        className={cn(
                          'group flex h-14 items-center justify-center gap-2 self-start rounded-card transition-all duration-150',
                          // Resting: already tinted, so the tile reads as an available action rather
                          // than as a disabled placeholder that only wakes up when you find it.
                          'border border-dashed border-brand-400/70 bg-brand-50/70 text-brand-700',
                          'dark:border-brand-500/50 dark:bg-brand-900/20 dark:text-brand-300',
                          // Hover is a DIFFERENT move, not "the tint arrives": the outline solidifies,
                          // the fill deepens and the tile lifts.
                          'hover:border-solid hover:border-brand-500 hover:bg-brand-100 hover:shadow-soft dark:hover:bg-brand-900/35',
                        )}
                      >
                        <Plus className="size-4 transition-transform duration-150 group-hover:rotate-90" />
                        <span className="type-label-strong">Add another account</span>
                      </button>
                    </div>
                  )}
              </>
            ),
          },
        ]}
      />

      <Modal
        title={editing ? 'Edit bank account' : 'Add bank account'}
        open={open}
        onOpenChange={(o) => { if (!o) setOpen(false); }}
        width={720}
        footer={<>
          <Button variant="default" onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="primary" loading={saveMut.isPending} onClick={submit}>{editing ? 'Save' : 'Add'}</Button>
        </>}
      >
        <Form
          key={editing ? `edit-${editing.id}` : 'add'}
          form={form}
          layout="vertical"
          requiredMark="optional"
          /* MySQL hands `tinyint` back as 0/1, and a switch seeded with 1 reports `aria-checked="1"`
             — not a value the ARIA switch role defines, so a screen reader cannot say whether the
             account is active. Coerced where the form is seeded, as the card above does for display. */
          initialValues={editing
            ? { ...editing, isActive: !!editing.isActive, isPrimary: !!editing.isPrimary }
            : { isActive: true, isPrimary: false }}
        >
          <Row gutter={16}>
            <Col span={16}>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="accountHolderName" label="Account holder name" rules={[{ required: true }]}>
                    <Input placeholder="e.g. Green Bay Biotech Pvt Ltd" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="bankName" label="Bank name" rules={[{ required: true }]}>
                    <Input placeholder="e.g. HDFC Bank" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="accountNumber" label="Account number" rules={[{ required: true }]}>
                    <Input placeholder="Account number" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="ifsc"
                    label="IFSC"
                    rules={[{ required: true }, { pattern: /^[A-Za-z]{4}0[A-Za-z0-9]{6}$/, message: 'Invalid IFSC (e.g. HDFC0001234)' }]}
                    normalize={(v) => (v ? String(v).toUpperCase() : v)}
                  >
                    <Input placeholder="e.g. HDFC0001234" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="branch" label="Branch">
                    <Input placeholder="Branch (optional)" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="label" label="Label">
                    <Input placeholder="e.g. HDFC Current A/c (optional)" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item name="upiId" label="UPI ID" extra="The QR is generated from this automatically (unless you upload an image below).">
                    <Input placeholder="e.g. greenbay@hdfcbank" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item name="notes" label="Notes">
                    <Input.TextArea rows={2} placeholder="Anything else to show with the bank details (optional)" />
                  </Form.Item>
                </Col>
              </Row>
            </Col>

            <Col span={8}>
              <Form.Item label="UPI QR">
                <div className="mb-2 grid place-items-center rounded-card border border-dashed border-line bg-surface-2 p-2">
                  {upiQrUrl ? (
                    <Image src={upiQrUrl} width={120} height={120} style={{ objectFit: 'contain' }} />
                  ) : previewUpi ? (
                    <div className="rounded bg-white p-1.5"><QRCode value={previewUpi} size={120} bordered={false} /></div>
                  ) : (
                    <span className="py-8 text-center type-caption text-subtle">Enter a UPI ID or upload a QR image</span>
                  )}
                </div>
                <Form.Item name="upiQrUrl" noStyle>
                  <FileUploadField category="payment-qr" buttonLabel="Upload QR image" preview={false} size="small" />
                </Form.Item>
                <span className="type-caption text-subtle">Optional — overrides the generated QR (e.g. a bank/PhonePe QR).</span>
              </Form.Item>
              <Form.Item name="isPrimary" label="Primary account" valuePropName="checked" tooltip="Shown on all invoices &amp; proforma">
                <Switch />
              </Form.Item>
              <Form.Item name="isActive" label="Active" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          {!editing && rows.length === 0 && (
            <Alert type="info" showIcon message="This first account is automatically set as the primary account shown on documents." />
          )}
        </Form>
      </Modal>
    </div>
  );
}
